import apiClient, { getAccessToken } from './api'

const MAX_BACKOFF_MS = 30000
const DEFAULT_POLL_INTERVAL_MS = 10000

class RealtimeEventService {
  constructor() {
    this.eventSource = null
    this.eventSourceFactory = null
    this.enabled = false
    this.status = 'idle'
    this.listeners = new Set()
    this.statusListeners = new Set()
    this.seenEventIds = new Set()
    this.seenNotificationIds = new Set()
    this.reconnectTimer = null
    this.pollTimer = null
    this.backoffMs = 1000
    this.pollingHandler = null
    this.tokenProvider = () => getAccessToken()
  }

  subscribe(listener) {
    if (typeof listener !== 'function') return () => {}
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  subscribeStatus(listener) {
    if (typeof listener !== 'function') return () => {}
    this.statusListeners.add(listener)
    return () => this.statusListeners.delete(listener)
  }

  getStatus() {
    return this.status
  }

  start() {
    if (this.enabled) return
    this.enabled = true
    this.updateStatus('connecting')
    this.open()
  }

  stop() {
    this.enabled = false
    this.disconnectCurrent()
    this.clearReconnectTimer()
    this.stopPollingFallback()
    this.updateStatus('idle')
  }

  setPollingFallbackHandler(handler) {
    this.pollingHandler = typeof handler === 'function' ? handler : null
  }

  setEventSourceFactoryForTests(factory) {
    this.eventSourceFactory = typeof factory === 'function' ? factory : null
  }

  setTokenProviderForTests(provider) {
    this.tokenProvider = typeof provider === 'function' ? provider : () => getAccessToken()
  }

  open() {
    if (!this.enabled) return

    const token = String(this.tokenProvider?.() || '').trim()
    if (!token) {
      this.updateStatus('error')
      this.enterFallbackPolling()
      this.scheduleReconnect()
      return
    }

    if (typeof window === 'undefined' || typeof window.EventSource !== 'function') {
      this.updateStatus('error')
      this.enterFallbackPolling()
      this.scheduleReconnect()
      return
    }

    const base = apiClient.defaults.baseURL || '/api'
    const url = new URL(`${base.replace(/\/$/, '')}/realtime/events`, window.location.origin)
    url.searchParams.set('access_token', token)

    const buildEventSource = this.eventSourceFactory || ((address) => new window.EventSource(address, { withCredentials: true }))
    const source = buildEventSource(url.toString())
    this.eventSource = source

    source.onopen = () => {
      this.backoffMs = 1000
      this.stopPollingFallback()
      this.updateStatus('connected')
    }

    source.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data)
        if (!parsed || typeof parsed !== 'object') return

        const eventId = String(parsed.id || '')
        if (eventId) {
          if (this.seenEventIds.has(eventId)) return
          this.seenEventIds.add(eventId)
          if (this.seenEventIds.size > 1000) {
            const first = this.seenEventIds.values().next().value
            if (first) this.seenEventIds.delete(first)
          }
        }

        const notificationId = String(parsed?.notification?.id || '')
        if (notificationId && parsed?.type === 'notification.created') {
          if (this.seenNotificationIds.has(notificationId)) return
          this.seenNotificationIds.add(notificationId)
          if (this.seenNotificationIds.size > 1000) {
            const first = this.seenNotificationIds.values().next().value
            if (first) this.seenNotificationIds.delete(first)
          }
        }

        this.listeners.forEach((listener) => listener(parsed))
      } catch {
        // Ignore malformed realtime event chunks.
      }
    }

    source.onerror = () => {
      this.disconnectCurrent()
      this.updateStatus('error')
      this.enterFallbackPolling()
      this.scheduleReconnect()
    }
  }

  updateStatus(status) {
    if (this.status === status) return
    this.status = status
    this.statusListeners.forEach((listener) => listener(status))
  }

  disconnectCurrent() {
    if (!this.eventSource) return
    this.eventSource.close()
    this.eventSource = null
  }

  clearReconnectTimer() {
    if (!this.reconnectTimer) return
    clearTimeout(this.reconnectTimer)
    this.reconnectTimer = null
  }

  scheduleReconnect() {
    if (!this.enabled || this.reconnectTimer) return
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.open()
      this.backoffMs = Math.min(MAX_BACKOFF_MS, this.backoffMs * 2)
    }, this.backoffMs)
  }

  async pollFallbackOnce() {
    if (typeof this.pollingHandler !== 'function') return
    try {
      await this.pollingHandler()
    } catch {
      // Keep fallback loop alive despite network polling errors.
    }
  }

  enterFallbackPolling() {
    if (!this.enabled) return
    this.updateStatus('fallback_polling')
    if (this.pollTimer) return

    void this.pollFallbackOnce()
    this.pollTimer = setInterval(() => {
      void this.pollFallbackOnce()
    }, DEFAULT_POLL_INTERVAL_MS)
  }

  stopPollingFallback() {
    if (!this.pollTimer) return
    clearInterval(this.pollTimer)
    this.pollTimer = null
  }
}

const realtimeEventService = new RealtimeEventService()

export default realtimeEventService
